import {
  Autocomplete,
  Box,
  Button,
  Card, 
  CircularProgress, 
  debounce,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import { useCallback, useEffect, useMemo } from 'react';
import { PaginateQueryWithStats } from 'src/api/pagination/dtos';
import { wait } from 'src/utils/timers';
import useObjectState from 'src/hooks/useObjectState';
import PatientRow from './PatientRow';
import { LoadingTableRow, TableHeadCustom } from 'src/components/table';
import useTable from 'src/hooks/useTable';
import FilterDrawer from 'src/components/FilterDrawer';
import Iconify from 'src/components/Iconify';
import TransferManagingOrg from './TransferManagingOrg';
import { WrappedOrganization } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { ValueSetComposeIncludeConcept } from 'src/careflow/ph-core/fhir-client/resources';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { TableToolbar } from 'src/careflow/common';
import { translateTableHead } from 'src/careflow/helpers/common';
import { TABLE_HEAD_FHIR_ORG_PATIENT_LIST } from 'src/careflow/common/table-head';
import NoResultsTableRow from 'src/careflow/common/NoResultsTableRow';
import { useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { PatientService } from 'src/crs/patient/services/PatientService';

type Props = {
  organization: {
    main: WrappedOrganization | null;
    partOf: WrappedOrganization | null;
    childs: WrappedOrganization[] | null;
  };
  translate: any;
};

interface PatientListState {
  isLoading: boolean;
  data: any;
  search: string;
  includeInactive: boolean;
  enrollment?: ValueSetComposeIncludeConcept | null | undefined;
  programType?: ValueSetComposeIncludeConcept | null | undefined;
  organizations?: { name: string | undefined; fhirId: string | undefined; }[];
  totalItems: number | null;
  openFilterDrawer: boolean;
  openOrgModal: boolean;
  patientSelected: any;
};

export function PatientList({ organization, translate }: Props) {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable({
    defaultRowsPerPage: 5,
    defaultOrderBy: 'id',
    initialIndex: 1,
    defaultCurrentPage: 1,
  });

  const [{
    isLoading,
    data,
    search,
    includeInactive,
    enrollment,
    programType,
    organizations,
    totalItems,
    openFilterDrawer,
    openOrgModal,
    patientSelected
  }, updateState] = useObjectState<PatientListState>({
    isLoading: false,
    data: [],
    search: '',
    includeInactive: false,
    enrollment: null,
    programType: null,
    organizations: [],
    totalItems: null,
    openFilterDrawer: false,
    openOrgModal: false,
    patientSelected: null
  });

  const getAllOrg = useMemo(() => {
    return [
      { fhirId: organization?.main?.id, name: organization?.main?.name },
      { fhirId: organization?.partOf?.id, name: organization?.partOf?.name},
      ...(organization?.childs?.map(({ id, name }) => ({ fhirId: id, name })) ?? [])
    ].filter(({ fhirId }) => Boolean(fhirId));
  }, [organization]);

  const getPaginateQuery = (
    page: number,
    rowsPerPage: number,
    search: string,
    orderBy: string,
    order: string,
    includeInactive: boolean,
    enrollment?: ValueSetComposeIncludeConcept | null | undefined,
    organizations?: {name: string | undefined, fhirId: string | undefined }[],
    programType?: ValueSetComposeIncludeConcept | null | undefined
  ) => {
    const paginateQuery: PaginateQueryWithStats = { page: page, limit: rowsPerPage };
    if (search) {
      paginateQuery.search = search;
    }
    paginateQuery.filter = {};

    if(!includeInactive){
      paginateQuery.filter.indexedStatus = "Active";
    }

    if (order && orderBy) {
      paginateQuery.sortBy = [[orderBy, order.toUpperCase()]];
    }

    if (enrollment?.code) {
      paginateQuery.filter.enrollment = enrollment.code;
    } else if (paginateQuery.filter.enrollment) {
      delete paginateQuery.filter.enrollment;
    }

    if(organizations && organizations?.length > 0){
      paginateQuery.filter.indexedManagingOrgUuid = `$in:${organizations
        .map(({ fhirId }) => fhirId)
        .join(',')}`;
    }

    if (programType?.code) {
      paginateQuery.filter.programType = programType.code;
    } else if (paginateQuery.filter.programType) {
      delete paginateQuery.filter.programType;
    }

    return paginateQuery;
  };

  const fetchPatientList = async (
    page: number,
    rowsPerPage: number,
    search: string,
    orderBy: string,
    order: string,
    includeInactive: boolean,
    enrollment?: ValueSetComposeIncludeConcept | null | undefined,
    organizations?: {name: string | undefined, fhirId: string | undefined }[],
    programType?: ValueSetComposeIncludeConcept | null | undefined
  ) => {

    updateState({ isLoading: true });
    await wait();
    const paginateQuery = getPaginateQuery(
      page,
      rowsPerPage,
      search,
      orderBy,
      order,
      includeInactive,
      enrollment,
      organizations,
      programType
    );
    
    const patients = await PatientService.getAll(paginateQuery);

    const totalItems = patients?.meta?.totalItems 
      ? patients?.meta?.totalItems 
      : 0

    if (!patients) {
      updateState({ isLoading: false });
      return;
    }

    updateState({ 
      data: patients?.data, 
      totalItems: totalItems,
      isLoading: false
    });
  };

  const getPatientList = useCallback(debounce(fetchPatientList, 600), []);

  useEffect(() => {
    getPatientList(
      page,
      rowsPerPage,
      search,
      orderBy,
      order,
      includeInactive,
      enrollment,
      getAllOrg,
      programType
    );
  }, [
    page,
    rowsPerPage,
    search,
    orderBy,
    getAllOrg,
  ]);

  const handlePageChange = (event: unknown, newPage: number) => {
    onChangePage(event, newPage + 1);
  };

  const handleSearch = (query: string) => {
    updateState({ search: query });
    setPage(1);
  };

  const togglePatientSelection = (row: any) => {
    updateState({
      patientSelected: row ? row : null
    });
  };

  const isAllowedToTransfer = checkAclValidation({ 
    acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.TRANSFER_PATIENT.VIEW]
  });

  return (
    <Card sx={{ width: '100%', height: '100%'}}>
      <Stack direction="row" sx={{ py: 2, px: 2 }} justifyContent='space-between'>
        <TableToolbar filterMrn={search} onFilterMrn={handleSearch} />
        <Tooltip title="Filter">
          <IconButton
            onClick={() => {
              updateState({ openFilterDrawer: true });
            }}
            sx={{ height: '40px' }}
            disabled={isLoading}
          >
            <FilterListIcon htmlColor="#919eab" />
          </IconButton>
        </Tooltip>
      </Stack>
      <TableContainer>
        <Table size={dense ? 'small' : 'medium'}>
          <TableHeadCustom
            headLabel={translateTableHead(TABLE_HEAD_FHIR_ORG_PATIENT_LIST, translate, 'translations')}
            rowCount={organization?.childs?.length}
          />

          <TableBody>
            {!isLoading ? (
              data?.length ? (
                data.map((row) => 
                  <PatientRow 
                    key={row.indexedPatienId}
                    row={row}
                    translate={translate}
                    menuItems={
                      <MenuItem
                        onClick={() => {
                          togglePatientSelection(row);
                          updateState({ openOrgModal: true });
                        }}
                        disabled={!isAllowedToTransfer}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Iconify icon={'tabler:transfer'} />
                          <Typography variant="body2">
                            {translate('details.patient.transfer.buttonList')}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    }
                  />
                )
              ) : (
                <NoResultsTableRow
                  colSpan={TABLE_HEAD_FHIR_ORG_PATIENT_LIST.length}
                  text={translate('dialogs.listNotFound')}
                />
              )
            ) : (
              <LoadingTableRow colSpan={TABLE_HEAD_FHIR_ORG_PATIENT_LIST.length} />
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 10, 25]}
          count={totalItems ? totalItems : 0}
          rowsPerPage={rowsPerPage}
          page={page - 1}
          onPageChange={handlePageChange}
          onRowsPerPageChange={onChangeRowsPerPage}
          sx={{ borderTop: 0 }}
        />

        <FormControlLabel
          control={<Switch checked={dense} onChange={onChangeDense} />}
          label="Dense"
          sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
        />
      </Box>
      <FilterDrawer
        title={translate('details.patient.titleFilter')}
        open={openFilterDrawer}
        onApplyButtonClick={() => {
          const orgs = organizations?.length 
            ? organizations 
            : getAllOrg;

          getPatientList(
            page,
            rowsPerPage,
            search,
            orderBy,
            order,
            includeInactive,
            enrollment,
            orgs,
            programType
          );
          updateState({ openFilterDrawer: false, patientSelected: null })
        }}
        onCloseIconButtonClick={() => 
          updateState({ openFilterDrawer: false, patientSelected: null })
        }
        anchor={'right'}
        onClearAllButtonClick={() => {
          updateState({
            includeInactive: false,
            enrollment: null,
            programType: null,
            organizations: [],
            patientSelected: null
          })
        }}
      >
        <PatientFilterList 
          open={openFilterDrawer}
          enrollment={enrollment}
          includeInactive={includeInactive}
          organizations={organizations}
          programType={programType}
          orgList={getAllOrg}
          translate={translate}
          updateState={updateState}
        />
      </FilterDrawer>
      
      <TransferManagingOrg 
        open={openOrgModal}
        translate={translate}
        patient={patientSelected}
        onClose={() => updateState({ openOrgModal: false })}
        onSelectRow={(row) => togglePatientSelection(row)}
        onConfirm={() => getPatientList(
          page,
          rowsPerPage,
          search,
          orderBy,
          order,
          includeInactive,
          enrollment,
          getAllOrg,
          programType
        )}
      />
    </Card>
  );
}

const PatientFilterList = ({
  open,
  enrollment,
  organizations,
  programType,
  includeInactive,
  updateState,
  translate,
  orgList,
}: {
  open: boolean;
  enrollment: any;
  organizations: any;
  programType: any;
  includeInactive: boolean;
  updateState: any;
  translate: any;
  orgList: { name: string | undefined; fhirId: string | undefined; }[];
}) => {

  const [episodeOfCareStatusVS, { isLoading: isEpisodeOfCareStatusVSLoading }] = useValueSet(
    'episode-of-care-status', { map: ValueSetWrapper, autofetch: !!open }
  );

  const [episodeOfCareTypeVS, { isLoading: isEpisodeOfCareTypeVSLoading }] = useValueSet(
    'episodeofcare-type', { map: ValueSetWrapper, autofetch: !!open}
  );

  function getSelectedEnrollment() {
    if (!enrollment) {
      return null;
    }

    const enrollmentF = episodeOfCareStatusVS?.asListAll?.()?.find((item) => item.code === enrollment?.code);

    if (enrollment) {
      return enrollmentF;
    }

    return null;
  }

  const selectedEnrollment = useMemo(getSelectedEnrollment, [episodeOfCareStatusVS, enrollment]);

  return (
    <Box py={3}>
      <Box>
        <Grid item sx={{ marginTop: 1 }} xs={12}>
          <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
            {translate('details.patient.enrollment', 'crs')} Status
          </Typography>
          <Autocomplete
            id="combo-box-enrollment"
            value={selectedEnrollment}
            loading={isEpisodeOfCareStatusVSLoading}
            fullWidth
            onChange={(_: React.SyntheticEvent, value: ValueSetComposeIncludeConcept | null) => {
              updateState((prevGridState: any) => ({
                ...prevGridState,
                enrollment: value,
              }));
            }}
            options={episodeOfCareStatusVS?.asListAll?.() ?? []}
            getOptionLabel={({ display }: ValueSetComposeIncludeConcept) => display ?? ''}
            renderInput={(params) => (
              <TextField
                label={translate('details.patient.enrollment')}
                {...params}
              />
            )}
            renderOption={(props, option, index) => (
              <li {...props} key={index + option.code}>
                {`${option.display}`}
              </li>
            )}
          />
        </Grid>
        <br />
        <Grid item sx={{ marginTop: 1 }} xs={12}>
          <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
            {translate('details.patient.organizations')}
          </Typography>
          <Autocomplete
            id="combo-box-organization"
            value={organizations ?? []}
            fullWidth
            disabled={!orgList?.length}
            multiple
            onChange={(_: React.SyntheticEvent, org: any) => {
              updateState((prevGridState: any) => ({
                ...prevGridState,
                organizations: org,
              }));
            }}
            options={orgList ?? []}
            getOptionLabel={({ name }) => name ?? ''}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={translate('details.patient.organizations')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {!orgList?.length ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <br />

        <Grid item sx={{ marginTop: 1 }} xs={12}>
          <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
            Enrollment Program
          </Typography>
          <Autocomplete
            id="combo-box-program-type"
            value={programType || null}
            loading={isEpisodeOfCareTypeVSLoading}
            fullWidth
            onChange={(_: React.SyntheticEvent, value: ValueSetComposeIncludeConcept | null) => {
              updateState((prevGridState: any) => ({
                ...prevGridState,
                programType: value,
              }));
            }}
            options={episodeOfCareTypeVS?.asListAll?.() ?? []}
            getOptionLabel={({ display }: ValueSetComposeIncludeConcept) => display ?? ''}
            renderInput={(params) => (
              <TextField
                label={translate('details.patient.programType')}
                placeholder={translate('details.patient.programType')}
                {...params}
              />
            )}
            renderOption={(props, option, index) => (
              <li {...props} key={index + option.code}>
                {`${option.display}`}
              </li>
            )}
            noOptionsText={translate('details.patient.programTypeNoOptions')}
            loadingText={translate('details.patient.programTypeLoadingText')}
          />
        </Grid>
        <br />

        <Grid item sx={{ marginTop: 1 }} xs={12}>
          <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
            {translate('details.patient.includeInactive')}
          </Typography>
          <Switch
            name="includeInactive"
            onChange={(_, includeInactive) => {
              updateState((prevGridState: any) => ({
                ...prevGridState,
                includeInactive: includeInactive,
              }));
            }}
            checked={includeInactive}
          />
        </Grid>
      </Box>
    </Box>
  )
}